// The reset ritual's timer: device-local, a deadline in wall-clock time (never
// a counter that drifts), the audio and the alarm made here from nothing (no
// files), and the Day record written at both ends through lib/wellbeing so a
// closed app still gets the push. One at a time.
import { create } from 'zustand'
import type { ResetSize } from '@/kinds/day/schema'
import { RESET_SIZES } from '@/kinds/day/schema'
import { beginReset, endReset } from '@/lib/wellbeing'

export type ResetAudio = 'noise' | 'none'

interface ResetState {
  status: 'idle' | 'running' | 'done'
  date: string | null
  size: ResetSize | null
  audio: ResetAudio
  /** ms since epoch */
  deadline: number
  startedAt: number
  start: (date: string, size: ResetSize, audio: ResetAudio) => Promise<void>
  /** stopped early: logged as not completed */
  stop: () => Promise<void>
  /** the deadline passed: logged as completed, the alarm sounds; idempotent */
  finish: () => Promise<void>
  /** the "stand up" line was seen */
  dismiss: () => void
  toggleAudio: () => void
}

const KEY = 'reset'

const read = (): Pick<ResetState, 'status' | 'date' | 'size' | 'audio' | 'deadline' | 'startedAt'> => {
  const none = { status: 'idle' as const, date: null, size: null, audio: 'noise' as const, deadline: 0, startedAt: 0 }
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return none
    const j = JSON.parse(raw) as Partial<Record<'status' | 'date' | 'size' | 'audio' | 'deadline' | 'startedAt', unknown>>
    const audio: ResetAudio = j.audio === 'none' ? 'none' : 'noise'
    if (j.status !== 'running' && j.status !== 'done') return { ...none, audio }
    const size = typeof j.size === 'string' && j.size in RESET_SIZES ? (j.size as ResetSize) : null
    return {
      status: j.status,
      date: typeof j.date === 'string' ? j.date : null,
      size,
      audio,
      deadline: typeof j.deadline === 'number' ? j.deadline : 0,
      startedAt: typeof j.startedAt === 'number' ? j.startedAt : 0,
    }
  } catch {
    return none
  }
}

const write = (s: ResetState) => {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ status: s.status, date: s.date, size: s.size, audio: s.audio, deadline: s.deadline, startedAt: s.startedAt }),
    )
  } catch {
    /* private mode: the timer still runs for the session */
  }
}

// ---- audio: brown noise looped from a buffer made once, and a firm alarm

let ctx: AudioContext | null = null
let noise: AudioBufferSourceNode | null = null
let noiseGain: GainNode | null = null

const context = () => {
  if (!ctx) ctx = new AudioContext()
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

/** Brown noise: a random walk, high-passed a little so it does not wander off. */
const brownBuffer = (c: AudioContext, seconds = 8): AudioBuffer => {
  const n = Math.floor(c.sampleRate * seconds)
  const buf = c.createBuffer(2, n, c.sampleRate)
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch)
    let last = 0
    for (let i = 0; i < n; i++) {
      const white = Math.random() * 2 - 1
      last = (last + 0.02 * white) / 1.02
      data[i] = last * 3.5
    }
  }
  return buf
}

export const startNoise = () => {
  try {
    const c = context()
    if (noise) return
    const src = c.createBufferSource()
    src.buffer = brownBuffer(c)
    src.loop = true
    const g = c.createGain()
    g.gain.setValueAtTime(0.0001, c.currentTime)
    g.gain.exponentialRampToValueAtTime(0.35, c.currentTime + 2)
    src.connect(g).connect(c.destination)
    src.start()
    noise = src
    noiseGain = g
  } catch {
    /* no audio here */
  }
}

export const stopNoise = () => {
  try {
    if (noise && noiseGain && ctx) {
      noiseGain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.4)
      const s = noise
      window.setTimeout(() => s.stop(), 1500)
    }
  } catch {
    /* fine */
  }
  noise = null
  noiseGain = null
}

/** Firm: three rising tones, three times, and the phone shakes with it. */
export const alarm = () => {
  try {
    const c = context()
    const t0 = c.currentTime
    for (let rep = 0; rep < 3; rep++) {
      ;[440, 554.37, 659.25].forEach((f, i) => {
        const o = c.createOscillator()
        const g = c.createGain()
        const at = t0 + rep * 1.1 + i * 0.22
        o.type = 'triangle'
        o.frequency.value = f
        g.gain.setValueAtTime(0.0001, at)
        g.gain.exponentialRampToValueAtTime(0.4, at + 0.02)
        g.gain.exponentialRampToValueAtTime(0.0001, at + 0.5)
        o.connect(g).connect(c.destination)
        o.start(at)
        o.stop(at + 0.55)
      })
    }
  } catch {
    /* no audio here */
  }
  try {
    navigator.vibrate?.([300, 120, 300, 120, 600])
  } catch {
    /* fine */
  }
}

// the Day writes in order: the screen changes at once, the record follows, and
// a stop or a finish waits for the start's write before making its own
let writing: Promise<unknown> = Promise.resolve()
const after = (f: () => Promise<unknown>) => {
  writing = writing.catch(() => undefined).then(f)
  return writing
}

export const useReset = create<ResetState>((set, get) => ({
  ...read(),
  start: async (date, size, audio) => {
    const now = Date.now()
    const next = { status: 'running' as const, date, size, audio, deadline: now + RESET_SIZES[size] * 60_000, startedAt: now }
    set(next)
    write({ ...get(), ...next })
    if (audio === 'noise') startNoise()
    await after(() => beginReset(date, size, audio))
  },
  stop: async () => {
    const s = get()
    if (s.status !== 'running') return
    stopNoise()
    set({ status: 'idle', deadline: 0 })
    write(get())
    if (s.date) await after(() => endReset(s.date!, false))
  },
  finish: async () => {
    const s = get()
    if (s.status !== 'running' || s.deadline > Date.now()) return
    stopNoise()
    alarm()
    set({ status: 'done' })
    write(get())
    if (s.date) await after(() => endReset(s.date!, true))
  },
  dismiss: () => {
    set({ status: 'idle', deadline: 0, startedAt: 0 })
    write(get())
  },
  toggleAudio: () => {
    const s = get()
    const audio: ResetAudio = s.audio === 'noise' ? 'none' : 'noise'
    set({ audio })
    write(get())
    if (s.status === 'running') {
      if (audio === 'noise') startNoise()
      else stopNoise()
    }
  },
}))

/** Whether it is late enough that the awake version is the one to run. */
export const lateForSleep = (now: number) => new Date(now).getHours() >= 19
